import { resolve, dirname, join } from "path";
import Factories from "@cyclonedx/cyclonedx-library/Factories";
import Builders from "@cyclonedx/cyclonedx-library/Builders";
import Serialize from "./json-serializer.js";
import Models from "@cyclonedx/cyclonedx-library/Models";
import Spec from "@cyclonedx/cyclonedx-library/Spec";
import Enums from "@cyclonedx/cyclonedx-library/Enums";
import Utils from "@cyclonedx/cyclonedx-library/Utils";
import nodeFsAdapter from "../node-fs-adapter";

const toolPkgJson = {
  name: "@servicenow/rollup-browser-poc",
  version: "1.0.0",
};

const DEFAULT_OPTIONS = {
  specVersion: Spec.Version.v1dot5,
  rootComponentType: Enums.ComponentType.Application,
  outDir: "cyclonedx",
  outFilename: "bom",
  outFormats: ["json"],
  saveTimestamp: true,
  rootDir: "/",
  generateSerial: false,
  includeWellKnown: true,
  supplier: void 0,
  properties: void 0,
};

const PLUGIN_ID = "rollup-plugin-sbom";
function rollupPluginSbom(userOptions) {
  const options = {
    ...DEFAULT_OPTIONS,
    ...userOptions,
  };
  const cdxExternalReferenceFactory =
    new Factories.FromNodePackageJson.ExternalReferenceFactory();
  const cdxLicenseFactory = new Factories.LicenseFactory();
  const cdxPurlFactory = new Factories.FromNodePackageJson.PackageUrlFactory(
    "npm",
  );
  const cdxToolBuilder = new Builders.FromNodePackageJson.ToolBuilder(
    cdxExternalReferenceFactory,
  );
  const cdxComponentBuilder = new Builders.FromNodePackageJson.ComponentBuilder(
    cdxExternalReferenceFactory,
    cdxLicenseFactory,
  );
  const jsonSerializer = new Serialize.JsonSerializer(
    new Serialize.JSON.Normalize.Factory(
      Spec.SpecVersionDict[options.specVersion],
    ),
  );
  const metadata = new Models.Metadata({
    supplier:
      options.supplier &&
      convertOrganizationalEntityOptionToModel(options.supplier),
    properties:
      options.properties &&
      new Models.PropertyRepository(
        options.properties.map(
          ({ name, value }) => new Models.Property(name, value),
        ),
      ),
  });
  const bom = new Models.Bom({
    metadata,
  });

  const exists = async (filePath) => {
    try {
      const res = await options.fs.stat(filePath);
      return res.isFile();
    } catch {
      return false;
    }
  };

  async function getPackageJson(dir) {
    try {
      const rawPkg = await options.fs.readFile(
        resolve(dir, "package.json"),
        "utf8",
      );
      const pkg = JSON.parse(rawPkg.toString());
      // normalizePackageData(pkg);
      return pkg;
    } catch {
      return null;
    }
  }
  async function getCorrespondingPackageFromModuleId(
    modulePath,
    traversalLimit = 10,
  ) {
    if (traversalLimit === 0) {
      return Promise.resolve(null);
    }
    const folder = dirname(modulePath);
    const potentialPackagePath = join(folder, "./package.json");
    let pkgJson = null;
    if (await exists(potentialPackagePath)) {
      pkgJson = await getPackageJson(folder);
    }
    if (pkgJson !== null) {
      return pkgJson;
    }
    return await getCorrespondingPackageFromModuleId(
      folder,
      traversalLimit - 1,
    );
  }
  function convertOrganizationalEntityOptionToModel(option) {
    return new Models.OrganizationalEntity({
      name: option.name,
      url: new Set(option.url),
      contact: new Models.OrganizationalContactRepository(
        option.contact.map(
          (contact) => new Models.OrganizationalContact(contact),
        ),
      ),
    });
  }

  function registerPackageUrlOnComponent(component, factory) {
    if (component) {
      component.purl = factory.makeFromComponent(component);
      component.bomRef.value = component.purl?.toString();
    }
  }
  async function registerTools(bom, builder) {
    async function registerTool() {
      try {
        const pkgJson = toolPkgJson;
        if (pkgJson) {
          const tool = builder.makeTool(pkgJson);
          tool && bom.metadata.tools.add(tool);
        }
      } catch {}
    }
    await registerTool();
  }

  const registeredPackageIds = [];

  return {
    name: PLUGIN_ID,
    async buildStart() {
      options.fs = options.fs || globalThis.__rollupFs || nodeFsAdapter;
      try {
        const rootPkg = await getPackageJson(options.rootDir);
        if (rootPkg) {
          bom.metadata.component = cdxComponentBuilder.makeComponent(
            rootPkg,
            options.rootComponentType,
          );
          bom.metadata.component.version = rootPkg.version;
          registerPackageUrlOnComponent(bom.metadata.component, cdxPurlFactory);
        }
      } catch (err) {
        this.error(`could not find package.json in ${options.rootDir}`);
      }

      bom.metadata.lifecycles.add(Enums.LifecyclePhase.Build);
      if (options.saveTimestamp) {
        bom.metadata.timestamp = /* @__PURE__ */ new Date();
      }
      if (options.generateSerial) {
        bom.serialNumber = Utils.BomUtility.randomSerialNumber();
      }
      await registerTools(bom, cdxToolBuilder);
    },
    /**
     * Register only the effectively imported third party modules from `node_modules`
     */
    async moduleParsed(moduleInfo) {
      const nodeModuleImportedIds = moduleInfo.importedIds.filter(
        (entry) => entry.includes("node_modules") && !entry.startsWith("\0"),
      );
      const potentialComponents = await Promise.all(
        nodeModuleImportedIds.map((moduleId) => {
          if (!moduleId.includes("node_modules")) {
            return Promise.resolve(null);
          }
          return getCorrespondingPackageFromModuleId(moduleId);
        }),
      );
      const pkgs = potentialComponents.filter((entry) => !!entry);
      for (const pkg of pkgs) {
        const pkgId = `${pkg.name}@${pkg.version}`;
        if (registeredPackageIds.includes(pkgId)) {
          continue;
        }
        const component = cdxComponentBuilder.makeComponent(
          pkg,
          Enums.ComponentType.Library,
        );
        registerPackageUrlOnComponent(component, cdxPurlFactory);
        component && bom.components.add(component);
        registeredPackageIds.push(pkgId);
      }
    },
    /**
     * Finalize the SBOM and emit files
     */
    generateBundle() {
      const formatMap = {
        json: jsonSerializer,
      };
      options.outFormats.forEach((format) => {
        if (!formatMap[format]) {
          throw new Error(`Unsupported format: ${format}`);
        }
        this.emitFile({
          type: "asset",
          fileName: join(options.outDir, `${options.outFilename}.${format}`),
          needsCodeReference: false,
          source: formatMap[format].serialize(bom, {
            sortLists: false,
            space: "	",
          }),
        });
      });
      if (options.includeWellKnown) {
        this.emitFile({
          type: "asset",
          fileName: ".well-known/sbom",
          needsCodeReference: false,
          source: jsonSerializer.serialize(bom, {
            sortLists: false,
            space: "	",
          }),
        });
      }
    },
  };
}

export default rollupPluginSbom;
